import {
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import {
  Button,
  Empty,
  Flex,
  Modal,
  Spin,
  Switch,
  Table,
  Tooltip,
  Typography,
  message,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useApi from "../../utils/useApi";
import CameraEnablementHistoryModal from "./CameraEnablementHistoryModal";
import CreateCameras from "./CreateCameras";

const { Text } = Typography;

export default function ListCameras({ update, setUpdate }) {
  const queryClient = useQueryClient();
  const [messageApi, contextHolder] = message.useMessage();
  const [modal, contextModal] = Modal.useModal();

  const [camerasList, setCamerasList] = useState([]);
  const [isCamerasLoading, setIsCamerasLoading] = useState(true);

  const [cameraToEdit, setCameraToEdit] = useState({});
  const [getInfos, setGetInfos] = useState(false);
  const [confirmEdition, setConfirmEdition] = useState(false);
  const [cameraForHistory, setCameraForHistory] = useState(null);

  const { getCameras, removeCamera, setActiveCamera } = useApi();

  const loadData = useCallback(async () => {
    setIsCamerasLoading(true);
    try {
      const response = await getCameras();
      if (response?.data?.length) {
        const cameras = response.data.map((camera) => ({
          key: camera.id,
          name: camera.name || "",
          address: camera.address || "",
          image: camera.image || "",
          isActive: camera.is_active ?? true,
        }));
        setCamerasList(cameras);
      } else {
        setCamerasList([]);
      }
    } catch (error) {
      console.error("Erro ao carregar câmeras:", error);
      setCamerasList([]);
    } finally {
      setIsCamerasLoading(false);
      if (update) {
        setUpdate(false);
      }
    }
  }, [getCameras, setUpdate, update]);

  useEffect(() => {
    if (update || !camerasList.length) {
      loadData();
    }
  }, [update, loadData, camerasList.length]);

  const handleRemoveCamera = useCallback(
    (id) => {
      removeCamera(id).then(() => {
        messageApi.success("Câmera removida com sucesso!");
        setUpdate(true);
      });
    },
    [removeCamera, messageApi, setUpdate]
  );

  const confirmDelete = useCallback(
    (id) => {
      modal.confirm({
        title: "Tem certeza que deseja remover essa câmera?",
        content: "Essa ação afetará as detecções imeadiatamente",
        onOk: () => handleRemoveCamera(id),
      });
    },
    [modal, handleRemoveCamera]
  );

  const handleSetActive = useCallback(
    (id, nextActive) => {
      return setActiveCamera(id, nextActive)
        .then(() => {
          messageApi.success(
            nextActive ? "Câmera ativada." : "Câmera desativada."
          );
          loadData();
          queryClient.invalidateQueries({
            queryKey: ["camera-enablement-history", id],
          });
        })
        .catch(() => {
          messageApi.error("Não foi possível atualizar o status da câmera.");
        });
    },
    [setActiveCamera, messageApi, loadData, queryClient]
  );

  const confirmDeactivate = useCallback(
    (id) => {
      modal.confirm({
        title: "Tem certeza que deseja desativar essa câmera?",
        content:
          "A gravação para a partir do próximo ciclo de processamento, não imediatamente. A câmera continua cadastrada e pode ser reativada quando quiser.",
        onOk: () => handleSetActive(id, false),
      });
    },
    [modal, handleSetActive]
  );

  const handleCloseModal = useCallback(() => {
    setConfirmEdition(false);
    setCameraToEdit({});
  }, []);

  const collumns = [
    {
      title: "Título",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Endereço",
      dataIndex: "address",
      key: "address",
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          checkedChildren="Ativa"
          unCheckedChildren="Desativada"
          onChange={(checked) =>
            checked
              ? handleSetActive(record.key, true)
              : confirmDeactivate(record.key)
          }
        />
      ),
    },
    {
      title: "Histórico",
      dataIndex: "history",
      key: "history",
      render: (text, record) => (
        <Button
          icon={<HistoryOutlined />}
          onClick={() => setCameraForHistory(record)}
        />
      ),
    },
    {
      title: "Editar",
      dataIndex: "edit",
      key: "edit",
      render: (text, record) => (
        <Button
          onClick={() => {
            setCameraToEdit(record);
            setGetInfos(true);
          }}
          icon={<EditOutlined />}
        />
      ),
    },
    {
      title: "Remover",
      dataIndex: "remove",
      key: "remove",
      render: (text, record) => (
        <Button
          icon={<DeleteOutlined />}
          danger
          onClick={() => confirmDelete(record.key)}
        />
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      {contextModal}

      {isCamerasLoading ? (
        <Flex justify="center" align="center">
          <Spin />
        </Flex>
      ) : camerasList.length > 0 ? (
        <>
          <Table columns={collumns} dataSource={camerasList} />
          <Modal
            title="Edite as informações necessárias"
            centered
            open={!!Object.keys(cameraToEdit).length}
            onOk={() => setConfirmEdition(true)}
            onCancel={handleCloseModal}
            width="50%"
            maskClosable={false}>
            <CreateCameras
              setUpdate={setUpdate}
              editMode
              getInfos={getInfos}
              setGetInfos={setGetInfos}
              cameraInfos={cameraToEdit}
              confirmEdition={confirmEdition}
              setConfirmEdition={setConfirmEdition}
              setCameraToEdit={setCameraToEdit}
              messageApi={messageApi}
            />
          </Modal>
          {cameraForHistory && (
            <CameraEnablementHistoryModal
              camera={cameraForHistory}
              onClose={() => setCameraForHistory(null)}
            />
          )}
        </>
      ) : (
        <Empty description="Nenhuma câmera cadastrada" />
      )}
    </>
  );
}
